<template>
  <div :class="[labelPosition === 'side' ? 'flex no-wrap' : '']">
    <div
      :class="[labelPosition === 'side' ? 'q-mr-lg q-mt-xs' : '']"
      :style="{
        paddingTop: `${labelOffset[0]}px`,
        paddingRight: `${labelOffset[1]}px`,
      }"
    >
      <div class="row justify-between items-center">
        <p v-if="label" :class="computedLabelClasses">
          <span>{{ label }}</span>
          <span v-if="required || validations?.required" class="text-negative">
            *
          </span>
        </p>
        <q-separator v-if="label" color="neutral-40" />
        <slot name="actionComponent"></slot>
      </div>

      <p v-if="help" :class="helpClasses">
        <span v-html="help"></span>
      </p>
    </div>
    <q-input
      ref="component"
      v-model="computedModelValue"
      :bg-color="bgColor"
      :class="[labelPosition === 'side' ? 'col' : '']"
      :rules="validationRules"
      :style="{
        minWidth: computedInputWidth,
      }"
      :input-class="$attrs.type === 'textarea' ? '' : 'ellipsis'"
      v-bind="$attrs"
      no-error-icon
      outlined
    >
      <template
        v-for="(slot, index) of Object.keys($slots)"
        :key="index"
        #[slot]
      >
        <slot :name="slot"></slot>
      </template>
    </q-input>
  </div>
</template>

<script setup>
import { isEmpty } from "assets/scripts/utilities/helpers";
import validationsToRules from "assets/scripts/utilities/validations";

const emit = defineEmits(["update:modelValue"]);

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: "",
  },
  bgColor: {
    type: String,
    default: "neutral-10",
  },
  placeholderWidth: {
    type: Boolean,
    default: false,
  },
  label: {
    type: String,
    default: "",
  },
  labelClasses: {
    type: String,
    default: "text-sm text-neutral ellipsis",
  },
  labelPosition: {
    type: String,
    default: "top",
  },
  help: {
    type: String,
    default: "",
  },
  helpClasses: {
    type: String,
    default: "text-xs text-neutral-70 q-mb-xs",
  },
  required: {
    type: Boolean,
    default: false,
  },
  rules: {
    type: Array,
    default: () => {
      return [];
    },
  },
  validations: {
    type: Object,
    default: () => {
      return {};
    },
  },
  labelOffset: {
    type: Array,
    default: () => [0, 0],
  },
});

const attrs = useAttrs();

const computedModelValue = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    if (attrs.type === "number") {
      if (attrs.min !== undefined && attrs.max !== undefined) {
        if (value < attrs.min) {
          emit("update:modelValue", Math.max(attrs.min, value));
        } else if (value > attrs.max) {
          emit("update:modelValue", Math.min(attrs.max, value));
        } else {
          emit("update:modelValue", Number(value));
        }
      } else if (attrs.min !== undefined) {
        emit("update:modelValue", Math.max(attrs.min, value));
      } else if (attrs.max !== undefined) {
        emit("update:modelValue", Math.min(attrs.max, value));
      } else {
        emit("update:modelValue", Number(value));
      }
    } else {
      emit("update:modelValue", value);
    }
  },
});

const computedInputWidth = computed(() => {
  if (props.placeholderWidth) {
    return `calc(${attrs.placeholder.length}ch + ${attrs.placeholder.length}px)`;
  }

  return "auto";
});

const computedLabelClasses = computed(() => {
  return `${props.labelClasses} ${props.help ? "q-mb-none" : "q-mb-xs"}`;
});

const component = ref(null);

defineExpose({
  component,
});

const bindEventsOnNativeInput = () => {
  if (attrs?.onInput) {
    unref(component).nativeEl.addEventListener("input", attrs.onInput);
  }

  if (attrs?.onKeyUp) {
    unref(component).nativeEl.addEventListener("keyup", attrs.onKeyUp);
  }
};

onMounted(bindEventsOnNativeInput);

const validationRules = computed(() => {
  if (props.required || !isEmpty(props.validations) || props.rules.length) {
    return [
      ...validationsToRules({
        label: props.validations?.label || props.label,
        required: props.required || props.validations?.required,
        ...props.validations,
      }),
      ...props.rules,
    ];
  }

  return [];
});
</script>

<style lang="scss" scoped>
.q-field {
  :deep(.q-field__inner) {
    .q-field {
      &__control {
        height: 38px;
        overflow: hidden;

        &::before {
          border-color: $neutral-50;
        }

        &:not(.text-negative):hover::after {
          border-color: $primary-80;
        }

        &:not(.text-negative):focus-within::after {
          border-color: $primary;
        }
      }

      &__marginal {
        height: 38px;
      }

      &__native {
        width: 100%;

        &::placeholder {
          color: $neutral-60;
        }
      }

      &__suffix {
        color: $neutral-80;

        font-size: map-get($xs, font-size);
        line-height: map-get($xs, line-height);
      }
    }
  }

  &.q-textarea {
    :deep(.q-field__inner) {
      .q-field {
        &__control {
          height: 100%;
          min-height: 90px;
          padding-right: 0;

          &::after {
            height: auto;
          }
        }

        &__marginal {
          height: 56px;
          padding: 0 6px 0 0;
        }

        &__native {
          padding: 6px 12px 6px 0;
          resize: none;
        }
      }
    }
  }

  &.prepend-bg {
    :deep(.q-field__inner) {
      .q-field {
        &__control {
          padding: 0 12px 0 0;
        }

        &__prepend {
          background: $neutral-40;

          justify-content: center;

          min-width: 38px;

          padding: 0 6px;
        }

        &__native {
          padding-left: 12px;
        }
      }
    }
  }

  &--dark {
    :deep(.q-field__inner) {
      .q-field {
        &__control {
          &:not(.text-negative):focus-within::after {
            border-color: $neutral-10;
          }
        }

        &__native {
          &::placeholder {
            color: $neutral-50;
          }
        }
      }
    }
  }

  &--dense {
    :deep(.q-field__inner) {
      .q-field {
        &__control {
          height: 30px;
        }

        &__marginal {
          height: 30px;
        }
      }
    }

    &.prepend-bg {
      :deep(.q-field__inner) {
        .q-field {
          &__prepend {
            min-width: 30px;
          }
        }
      }
    }
  }

  &--disabled {
    :deep(.q-field__inner) {
      .q-field {
        &__control {
          background-color: $neutral-30;
        }
      }
    }
  }

  :deep(.q-field__after) {
    &.q-field {
      &__marginal {
        height: 40px;
      }
    }
  }
}
</style>
